import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Calendar, Award, Tag, Plus, X, Edit } from 'lucide-react';
import { FiArrowLeft, FiMail, FiPhone, FiCalendar, FiMapPin, FiPlus, FiAward, FiEdit, FiUser, FiSearch, FiDownload, FiTrash2, FiRefreshCw, FiEye, FiFileText } from 'react-icons/fi';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import CreateCertificateModal from '../components/CreateCertificateModal';
import Modal from '../components/Modal';
import apiClient, { endpoints } from '../api/client';
import EditCertificateModal from '../components/EditCertificateModal';
import RenewCertificateModal from '../components/RenewCertificateModal';
import EditUserModal from '../components/EditUserModal';
import DeleteItemModal from '../components/DeleteItemModal';

const UserDetail = () => {
  const { userId } = useParams();
  const { isLimitedAdmin } = useAuth();
  const [user, setUser] = useState(null);
  const [userTags, setUserTags] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCertificate, setShowAddCertificate] = useState(false);
  const [showAddTag, setShowAddTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [showTagModal, setShowTagModal] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagModalLoading, setTagModalLoading] = useState(false);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagForm, setNewTagForm] = useState({ name: '', type: 'informational' });
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [tagFormErrors, setTagFormErrors] = useState({});

  // Certificate Management States
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [certificateModalMode, setCertificateModalMode] = useState('view'); // 'view', 'edit', 'renew', 'delete'
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [isImage, setIsImage] = useState(false);

  const { showError, showSuccess } = useToast();

  useEffect(() => {
    if (typeof selectedCertificate?.file_url !== "string") return;

    const img = new Image();
    img.src = selectedCertificate.file_url;
    img.onload = () => setIsImage(true);
    img.onerror = () => setIsImage(false);
  }, [selectedCertificate?.file_url]);

  const fetchUserData = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch user details, certificates, and user tags in parallel
      const [userResponse, certificatesResponse, userTagsResponse] = await Promise.all([
        apiClient.get(endpoints.users.byId(userId)).catch(err => {
          console.error('Error fetching user:', err);
          throw new Error(`Failed to fetch user: ${err.response?.data?.message || err.message}`);
        }),
        apiClient.get(`${endpoints.certificates.list}?user_id=${userId}`).catch(err => {
          console.error('Error fetching certificates:', err);
          // Don't throw for certificates - they're optional
          return { data: { data: { certificates: [] } } };
        }),
        apiClient.get(endpoints.users.tags(userId)).catch(err => {
          console.error('Error fetching user tags:', err);
          // Don't throw for tags - they're optional
          return { data: { data: [] } };
        })
      ]);

      // Validate user response
      if (!userResponse?.data?.data) {
        throw new Error('Invalid user data received from server');
      }

      const userData = userResponse.data.data.user;
      const certificatesData = certificatesResponse?.data?.data || [];
      const userTagsData = userTagsResponse?.data?.data?.tags || [];

      // Merge user tags into user object
      userData.tags = userTagsData;

      setUser(userData);
      setCertificates(certificatesData);

    } catch (error) {
      console.error('Error fetching user data:', error);

      // Handle specific error types
      if (error.response?.status === 404) {
        showError('User not found');
        setUser(null);
      } else if (error.response?.status === 403) {
        showError('You do not have permission to view this user');
      } else if (error.response?.status === 401) {
        showError('Your session has expired. Please log in again.');
      } else {
        const errorMessage = error.message || 'Failed to load user data. Please try again.';
        showError(errorMessage);
      }

      // Reset state on error
      setUser(null);
      setCertificates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserData();
      fetchAvailableTags();
    }
  }, [userId, tagSearchTerm]);

  // Fetch available tags for the modal
  const fetchAvailableTags = async () => {
    try {
      const params = new URLSearchParams({
        limit: 100, // Get all tags for selection
        ...(tagSearchTerm && { search: tagSearchTerm })
      });

      const response = await apiClient.get(`/api/tags?${params}`);
      if (response.data.success) {
        setAvailableTags(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching tags:', err);
      showError('Failed to load available tags');
    }
  };

  // Enhanced tag management functions
  const handleOpenTagModal = () => {
    setShowTagModal(true);
    setSelectedTags([]);
    setTagSearchTerm('');
    fetchAvailableTags();
  };

  const handleCloseTagModal = () => {
    setShowTagModal(false);
    setSelectedTags([]);
    setTagSearchTerm('');
    setShowCreateTag(false);
    setNewTagForm({ name: '', type: 'informational' });
    setTagFormErrors({});
  };

  const handleTagSelection = (tag) => {
    setSelectedTags(prev => {
      const isSelected = prev.some(t => t.id === tag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== tag.id);
      } else {
        return [...prev, tag];
      }
    });
  };

  const handleAssignTags = async () => {
    if (selectedTags.length === 0) {
      showError('Please select at least one tag to assign');
      return;
    }

    setTagModalLoading(true);
    try {
      const promises = selectedTags.map(tag =>
        apiClient.post(endpoints.users.tags(userId), {
          tag_name: tag.name
        })
      );

      await Promise.all(promises);
      showSuccess(`Successfully assigned ${selectedTags.length} tag(s)`);
      handleCloseTagModal();
      fetchUserData(); // Refresh user data
    } catch (err) {
      console.error('Error assigning tags:', err);
      const errorMessage = err.response?.data?.message || 'Failed to assign tags';
      showError(errorMessage);
    } finally {
      setTagModalLoading(false);
    }
  };

  const validateTagForm = () => {
    const errors = {};

    if (!newTagForm.name.trim()) {
      errors.name = 'Tag name is required';
    } else if (newTagForm.name.length > 255) {
      errors.name = 'Tag name must be less than 255 characters';
    }

    if (!newTagForm.type) {
      errors.type = 'Tag type is required';
    }

    setTagFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateNewTag = async (e) => {
    e.preventDefault();

    if (!validateTagForm()) {
      return;
    }

    setTagModalLoading(true);
    try {
      const response = await apiClient.post(endpoints.tags.create, newTagForm);
      const newTag = response.data;

      fetchAvailableTags();

      // Reset form
      setNewTagForm({ name: '', type: 'informational' });
      setTagFormErrors({});
      setShowCreateTag(false);

      showSuccess('Tag created successfully');
    } catch (err) {
      console.error('Error creating tag:', err);
      const errorMessage = err.response?.data?.message || 'Failed to create tag';
      showError(errorMessage);
    } finally {
      setTagModalLoading(false);
    }
  };

  // Certificate Management Handlers
  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setCertificateModalMode('view');
    setShowCertificateModal(true);
  };

  const handleEditCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setCertificateModalMode('edit');
    setShowCertificateModal(true);
  };

  const handleRenewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setCertificateModalMode('renew');
    setShowCertificateModal(true);
  };

  const handleDeleteCertificate = async (certificate) => {
    setSelectedCertificate(certificate);
    setCertificateModalMode('delete');
    setShowCertificateModal(true);
  };

  const handleDownloadCertificate = async (certificate) => {
    if (!certificate.file_path) {
      showError('No file available for download');
      return;
    }

    try {
      const response = await apiClient.get(endpoints.certificates.download(certificate.id), {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${certificate.certificate_type}_${certificate.certificate_id || certificate.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('Certificate downloaded successfully');
    } catch (err) {
      console.error('Error downloading certificate:', err);
      const errorMessage = err.response?.data?.message || 'Failed to download certificate';
      showError(errorMessage);
    }
  };

  const handleCertificateCreated = (newCertificate) => {
    console.log(newCertificate)
    setCertificates(prev => [...prev, newCertificate]);
    setShowAddCertificate(false);
    showSuccess('Certificate added successfully');
  };

  const handleCertificateUpdated = (updatedCertificate) => {
    setCertificates(prev =>
      prev.map(cert =>
        cert.id === updatedCertificate.id ? updatedCertificate : cert
      )
    );
    setShowCertificateModal(false);
    setSelectedCertificate(null);
    showSuccess('Certificate updated successfully');
  };

  const handleUserUpdated = (updatedCertificate) => {
    setShowEditUserModal(false);
    fetchUserData()
  };

  const handleCloseCertificateModal = () => {
    setShowCertificateModal(false);
    setSelectedCertificate(null);
    setCertificateModalMode('view');
  };

  const handleRemoveTag = async (tagId) => {
    if (isLimitedAdmin()) return;

    try {
      const response = await apiClient.delete(`${endpoints.users.tags(userId)}/${tagId}`);

      if (response.data.success) {
        showSuccess('Tag removed successfully');
        fetchUserData(); // Refresh user data
      } else {
        showError(response.data.message || 'Failed to remove tag');
      }
    } catch (err) {
      console.error('Error removing tag:', err);
      const errorMessage = err.response?.data?.message || 'Failed to remove tag';
      showError(errorMessage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      expiring: 'bg-yellow-100 text-yellow-800',
      revoked: 'bg-gray-100 text-gray-800'
    };

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'
        }`}>
        {status}
      </span>
    );
  };

  const getTagBadge = (tag) => {
    // Handle both string and object tag formats
    const tagName = typeof tag === 'string' ? tag : (tag?.name || tag?.tag_name || 'Unknown');
    const tagId = typeof tag === 'object' ? (tag?.id || tag?.tag_id) : null;

    const tagClasses = {
      'HSI': 'bg-blue-100 text-blue-800',
      'AHA': 'bg-purple-100 text-purple-800',
      'Renewed': 'bg-green-100 text-green-800',
      'Do Not Call': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium mr-2 mb-2 ${tagClasses[tagName] || 'bg-gray-100 text-gray-800'
        }`}>
        {tagName}
        {!isLimitedAdmin() && tagId && (
          <button
            onClick={() => handleRemoveTag(tagId)}
            className="ml-1 text-red-500 hover:text-red-700"
            title="Remove tag"
          >
            ×
          </button>
        )}
      </span>
    );
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpiryIndicator = (expiryDate) => {
    const days = getDaysUntilExpiry(expiryDate);
    if (days === null) return null;

    if (days < 0) {
      return <span className="text-red-600 font-medium">Expired {Math.abs(days)} days ago</span>;
    } else if (days <= 30) {
      return <span className="text-yellow-600 font-medium">Expires in {days} days</span>;
    } else {
      return <span className="text-green-600">Expires in {days} days</span>;
    }
  };



  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary btn-pulse"></div>
        <span className="ml-3 text-gray-600">Loading user data...</span>
      </div>
    );
  }

  if (!user && !loading) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">User Not Found</h1>
        <p className="text-gray-600 mb-6">The user you're looking for doesn't exist or you don't have permission to view it.</p>
        <Link
          to="/users"
          className="inline-flex items-center px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-colors duration-200"
        >
          <FiArrowLeft className="mr-2 h-4 w-4" />
          Back to Clients
        </Link>
      </div>
    );
  }

  return (
    <>
      <div>
        {/* Header */}
        <div className="mb-8">
          <Link to="/users" className="inline-flex items-center text-brand-primary hover:text-brand-primary-dark btn-animate transition-all duration-200 mb-4">
            <FiArrowLeft className="mr-2 h-4 w-4" />
            Back to Clients
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {user.first_name} {user.last_name}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            User details and certificate management
          </p>
        </div>



        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* User Information */}
          <div className="lg:col-span-1">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  User Information
                </h3>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiMail className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.email || 'N/A'}</div>
                        <div className="text-sm text-gray-500">Email</div>
                      </div>
                    </div>
                    {!isLimitedAdmin() && (
                      <button
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Edit email"
                        onClick={() => setShowEditUserModal(true)}
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiPhone className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{user.phone || 'N/A'}</div>
                        <div className="text-sm text-gray-500">Phone</div>
                      </div>
                    </div>
                    {!isLimitedAdmin() && (
                      <button
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Edit phone"
                        onClick={() => setShowEditUserModal(true)}
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <FiUser className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {user.first_name && user.last_name
                            ? `${user.first_name} ${user.last_name}`
                            : user.first_name || user.last_name || 'N/A'
                          }
                        </div>
                        <div className="text-sm text-gray-500">Full Name</div>
                      </div>
                    </div>
                    {!isLimitedAdmin() && (
                      <button
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Edit name"
                        onClick={() => setShowEditUserModal(true)}
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center">
                    <FiCalendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{formatDate(user.created_at)}</div>
                      <div className="text-sm text-gray-500">Completion Date</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <FiMapPin className="h-5 w-5 text-gray-400 mr-3" />
                    <div className='flex-1'>
                      <div className="text-sm font-medium text-gray-900">{user.mailing_address ?? "N/A"}</div>
                      <div className="text-sm text-gray-500">Mailing Address</div>
                    </div>
                    {!isLimitedAdmin() && (
                      <button
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Edit name"
                        onClick={() => setShowEditUserModal(true)}
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {user.updated_at && user.updated_at !== user.created_at && (
                    <div className="flex items-center">
                      <FiCalendar className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{formatDate(user.updated_at)}</div>
                        <div className="text-sm text-gray-500">Last updated</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900 mb-2">Status</div>
                      {getStatusBadge(user.status || 'unknown')}
                    </div>
                    {!isLimitedAdmin() && (
                      <button
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Change status"
                        onClick={() => setShowEditUserModal(true)}
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-gray-900 mb-2">Role</div>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.role?.replace('_', ' ') || 'N/A'}
                      </span>
                    </div>
                    {!isLimitedAdmin() && (
                      <button
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                        title="Change role"
                        onClick={() => setShowEditUserModal(true)}
                      >
                        <FiEdit className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tags Section */}
            <div className="bg-white shadow rounded-lg mt-6">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Tags
                  </h3>
                  {!isLimitedAdmin() && (
                    <button
                      onClick={handleOpenTagModal}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
                    >
                      <FiPlus className="mr-1 h-4 w-4" />
                      Manage Tags
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap">
                  {user.tags && user.tags.length > 0 ? (
                    user.tags.map((tag, index) => (
                      <span key={index}>{getTagBadge(tag)}</span>
                    ))
                  ) : (
                    <span className="text-sm text-gray-500">No tags assigned</span>
                  )}
                </div>


              </div>
            </div>
          </div>

          {/* Certificates Section */}
          <div className="lg:col-span-2">
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Certificates ({certificates.length})
                  </h3>
                  {!isLimitedAdmin() && (
                    <button
                      onClick={() => setShowAddCertificate(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary btn-animate transition-all duration-200"
                    >
                      <FiPlus className="mr-2 h-4 w-4" />
                      Add Certificate
                    </button>
                  )}
                </div>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary btn-pulse"></div>
                </div>
              ) : certificates.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  <FiAward className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>No certificates found for this user</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Certificate Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Expiry Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {certificates.map((cert) => (
                        <tr key={cert.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {cert.type}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {cert.certificate_id || cert.id}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getStatusBadge(cert.status)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(cert.expiry_date)}
                            </div>
                            <div className="text-sm">
                              {getExpiryIndicator(cert.expiry_date)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              {/* View Certificate */}
                              <button
                                onClick={() => handleViewCertificate(cert)}
                                className="inline-flex items-center px-2 py-1 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg btn-animate transition-all duration-200"
                                title="View Certificate"
                              >
                                <FiEye className="h-4 w-4" />
                              </button>

                              {/* Download Certificate */}
                              {cert.file_path && (
                                <button
                                  onClick={() => handleDownloadCertificate(cert)}
                                  className="inline-flex items-center px-2 py-1 text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 rounded-lg btn-animate transition-all duration-200"
                                  title="Download Certificate"
                                >
                                  <FiDownload className="h-4 w-4" />
                                </button>
                              )}

                              {!isLimitedAdmin() && (
                                <>
                                  {/* Edit Certificate */}
                                  <button
                                    onClick={() => handleEditCertificate(cert)}
                                    className="inline-flex items-center px-2 py-1 text-yellow-600 hover:text-yellow-800 bg-yellow-50 hover:bg-yellow-100 rounded-lg btn-animate transition-all duration-200"
                                    title="Edit Certificate"
                                  >
                                    <FiEdit className="h-4 w-4" />
                                  </button>

                                  {/* Renew Certificate */}
                                  <button
                                    onClick={() => handleRenewCertificate(cert)}
                                    className="inline-flex items-center px-2 py-1 text-purple-600 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 rounded-lg btn-animate transition-all duration-200"
                                    title="Renew Certificate"
                                  >
                                    <FiRefreshCw className="h-4 w-4" />
                                  </button>

                                  {/* Delete Certificate */}
                                  <button
                                    onClick={() => handleDeleteCertificate(cert)}
                                    className="inline-flex items-center px-2 py-1 text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-lg btn-animate transition-all duration-200"
                                    title="Delete Certificate"
                                    disabled={certificateLoading}
                                  >
                                    <FiTrash2 className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tag Management Modal */}
      <Modal
        isOpen={showTagModal}
        onClose={handleCloseTagModal}
        title="Manage User Tags"
        size="lg"
      >
        <div className="space-y-6">
          {/* Current Tags */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-2">Current Tags</h4>
            <div className="flex flex-wrap">
              {user.tags && user.tags.length > 0 ? (
                user.tags.map((tag, index) => (
                  <span key={index}>{getTagBadge(tag)}</span>
                ))
              ) : (
                <span className="text-sm text-gray-500">No tags assigned</span>
              )}
            </div>
          </div>

          {/* Search Available Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Available Tags
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search tags..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200"
                value={tagSearchTerm}
                onChange={(e) => {
                  setTagSearchTerm(e.target.value);
                  fetchAvailableTags();
                }}
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
            </div>
          </div>

          {/* Available Tags Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-900">Available Tags</h4>
              <button
                type="button"
                onClick={() => setShowCreateTag(true)}
                className="text-sm text-brand-primary hover:text-brand-primary-dark font-medium"
              >
                + Add New Tag
              </button>
            </div>

            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableTags.length > 0 ? (
                <div className="space-y-2">
                  {availableTags
                    .filter(tag => !user.tags?.some(userTag =>
                      (typeof userTag === 'string' ? userTag : userTag.name) === tag.name
                    ))
                    .map(tag => (
                      <label key={tag.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedTags.some(t => t.id === tag.id)}
                          onChange={() => handleTagSelection(tag)}
                          className="rounded border-gray-300 text-brand-primary focus:ring-brand-primary"
                        />
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${tag.type === 'functional' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                          {tag.name}
                        </span>
                        <span className="text-xs text-gray-500">({tag.type})</span>
                      </label>
                    ))
                  }
                </div>
              ) : (
                <p className="text-sm text-gray-500 text-center py-4">
                  {tagSearchTerm ? 'No tags found matching your search.' : 'No available tags to assign.'}
                </p>
              )}
            </div>
          </div>

          {/* Create New Tag Form */}
          {showCreateTag && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Tag</h4>
              <form onSubmit={handleCreateNewTag} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tag Name *
                  </label>
                  <input
                    type="text"
                    value={newTagForm.name}
                    onChange={(e) => {
                      setNewTagForm(prev => ({ ...prev, name: e.target.value }));
                      if (tagFormErrors.name) {
                        setTagFormErrors(prev => ({ ...prev, name: '' }));
                      }
                    }}
                    className={`block w-full border rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 ${tagFormErrors.name ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter tag name"
                  />
                  {tagFormErrors.name && (
                    <p className="mt-1 text-sm text-red-600">{tagFormErrors.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tag Type *
                  </label>
                  <select
                    value={newTagForm.type}
                    onChange={(e) => {
                      setNewTagForm(prev => ({ ...prev, type: e.target.value }));
                      if (tagFormErrors.type) {
                        setTagFormErrors(prev => ({ ...prev, type: '' }));
                      }
                    }}
                    className={`block w-full border rounded-lg shadow-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary transition-all duration-200 ${tagFormErrors.type ? 'border-red-300' : 'border-gray-300'
                      }`}
                  >
                    <option value="informational">Informational</option>
                    <option value="functional">Functional</option>
                  </select>
                  {tagFormErrors.type && (
                    <p className="mt-1 text-sm text-red-600">{tagFormErrors.type}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    <strong>Informational:</strong> For categorization (e.g., "HSI", "AHA")<br />
                    <strong>Functional:</strong> For system behavior (e.g., "Do Not Call", "Priority")
                  </p>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateTag(false);
                      setNewTagForm({ name: '', type: 'informational' });
                      setTagFormErrors({});
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={tagModalLoading}
                    className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {tagModalLoading ? 'Creating...' : 'Add Tag'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Selected Tags Summary */}
          {selectedTags.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Selected Tags ({selectedTags.length})
              </h4>
              <div className="flex flex-wrap gap-1">
                {selectedTags.map(tag => (
                  <span key={tag.id} className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {tag.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Modal Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleCloseTagModal}
              className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignTags}
              disabled={selectedTags.length === 0 || tagModalLoading}
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-primary hover:bg-brand-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {tagModalLoading ? 'Assigning...' : `Assign ${selectedTags.length} Tag${selectedTags.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </Modal>

      {/* Certificate Management Modal */}
      <Modal
        isOpen={showCertificateModal && certificateModalMode === 'view'}
        onClose={handleCloseCertificateModal}
        title={
          certificateModalMode === 'view' ? 'Certificate Details' :
            certificateModalMode === 'edit' ? 'Edit Certificate' :
              'Renew Certificate'
        }
        size="lg"
      >
        {selectedCertificate && (
          <div className="space-y-6">
            {certificateModalMode === 'view' ? (
              /* View Mode */
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Certificate Type</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedCertificate.type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Certificate ID</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedCertificate.certificate_id || selectedCertificate.id}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedCertificate.status)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Issue Date</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedCertificate.issue_date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expiry Date</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedCertificate.expiry_date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Days Until Expiry</label>
                    <div className="mt-1">{getExpiryIndicator(selectedCertificate.expiry_date)}</div>
                  </div>
                </div>

                {selectedCertificate.notes && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedCertificate.notes}</p>
                  </div>
                )}

                {selectedCertificate.file_url && (
                  <div>
                    {
                      isImage ?
                        <img src={selectedCertificate.file_url} alt="file" className="w-full h-full object-cover" />
                        :
                        <a target="_blank" rel="noopener noreferrer" href={selectedCertificate.file_url} >
                          <div className="w-full h-full flex flex-row gap-4 items-center justify-center border-2 border-gray-300 p-4 rounded-xl">
                            <FiFileText className="inline h-10 w-10 mr-1" />
                            <p className="text-sm text-gray-800">
                              {selectedCertificate.file_url}
                            </p>
                          </div>
                        </a>
                    }
                  </div>
                )}

                <div className="flex justify-between pt-4 border-t border-gray-200">
                  <div className="flex space-x-3">
                    {selectedCertificate.file_path && (
                      <button
                        onClick={() => handleDownloadCertificate(selectedCertificate)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200"
                      >
                        <FiDownload className="mr-2 h-4 w-4" />
                        Download
                      </button>
                    )}
                    {!isLimitedAdmin() && (
                      <>
                        <button
                          onClick={() => setCertificateModalMode('edit')}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-all duration-200"
                        >
                          <FiEdit className="mr-2 h-4 w-4" />
                          Edit
                        </button>
                        <button
                          onClick={() => setCertificateModalMode('renew')}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200"
                        >
                          <FiRefreshCw className="mr-2 h-4 w-4" />
                          Renew
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    onClick={handleCloseCertificateModal}
                    className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            ) : (
              /* Edit/Renew Mode - Placeholder for future implementation */
              <div className="text-center py-8">
                <FiAward className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {certificateModalMode === 'edit' ? 'Edit Certificate' : 'Renew Certificate'}
                </h3>
                <p className="text-gray-500 mb-4">
                  {certificateModalMode === 'edit'
                    ? 'Certificate editing functionality will be implemented in the next phase.'
                    : 'Certificate renewal functionality will be implemented in the next phase.'
                  }
                </p>
                <button
                  onClick={handleCloseCertificateModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Add Certificate Modal */}
      {showAddCertificate && (
        <CreateCertificateModal
          isOpen={showAddCertificate}
          onClose={() => setShowAddCertificate(false)}
          onCertificateCreated={handleCertificateCreated}
          userId={userId}
        />
      )}

      {/* Edit Certificate Modal */}
      {showCertificateModal && certificateModalMode === 'edit' && (
        <EditCertificateModal
          data={selectedCertificate}
          isOpen={certificateModalMode === 'edit'}
          onClose={() => { setCertificateModalMode(''); setShowCertificateModal(false); }}
          onCertificateUpdated={handleCertificateUpdated}
          userId={userId}
        />
      )}

      {/* Renew Certificate Modal */}
      {showCertificateModal && certificateModalMode === 'renew' && (
        <RenewCertificateModal
          data={selectedCertificate}
          isOpen={certificateModalMode === 'renew'}
          onClose={() => { setCertificateModalMode(''); setShowCertificateModal(false); }}
          onCertificateUpdated={handleCertificateUpdated}
          userId={userId}
        />
      )}

      {/* Delete Certificate Modal */}
      {showCertificateModal && certificateModalMode === 'delete' && (
        <DeleteItemModal
          entity="certificate"
          url={endpoints.certificates.delete(selectedCertificate.id)}
          isOpen={certificateModalMode === 'delete'}
          onClose={() => { setCertificateModalMode(''); setShowCertificateModal(false); }}
          onItemDeleted={() => fetchUserData()}
        />
      )}

      {/* Edit User Modal */}
      {showEditUserModal && (
        <EditUserModal
          data={user}
          availableTags={availableTags}
          isOpen={showEditUserModal}
          onClose={() => setShowEditUserModal(false)}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </>
  );
};

export default UserDetail;